import React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { formatUnits } from 'viem';
import { toast } from 'react-toastify';
import { useAdmin, type LockDetails } from '@/hooks/useAdmin';
import { useGrantPages } from '@/hooks/useGrantPages';
import { useGrantSummary } from '@/hooks/useGrantSummary';
import { Category, categoryLabel } from '@/hooks/useWeb3';
import { displayedVestedAmount, payableCeiling } from '@/contracts/readState';
import { environment } from '@/config/environment';
import { Spinner } from '@/components/Spinner';

const date = (seconds: bigint, unset = 'Unset') => seconds === 0n
  ? unset
  : `${new Date(Number(seconds) * 1000).toISOString().replace('T', ' ').slice(0, 19)} UTC`;

const tokens = (amount: bigint) => {
  const [whole, fraction] = formatUnits(amount, environment.tokenDecimals).split('.');
  return `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction ? `.${fraction}` : ''} SAHARA`;
};

const fieldsStyle: React.CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20, margin: 0,
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="sh-label">{label}</dt>
    <dd className="sh-mono" style={{ margin: '8px 0 0', fontSize: 12, overflowWrap: 'anywhere' }}>
      {children}
    </dd>
  </div>
);

const Summary: React.FC<{ revision: number }> = ({ revision }) => {
  const { data, error, retry } = useGrantSummary(revision);
  const funding = data?.funding;
  const deposit = data && funding && funding.nextPeriodFunding > data.factoryBalance
    ? funding.nextPeriodFunding - data.factoryBalance : 0n;
  return (
    <section className="sh-card" aria-label="Grant summary">
      <h2 className="sh-h2" style={{ marginBottom: 20 }}>Summary</h2>
      {error ? <div role="alert" className="sh-banner sh-banner-danger">
        {error} <button type="button" className="sh-btn sh-btn-sm" onClick={retry}>Retry summary</button>
      </div> : !data ? <Spinner label="Reading all-grant summary" /> : <>
        <dl style={fieldsStyle}>
          <Field label="Total allocation">{tokens(data.totalAllocation)}</Field>
          <Field label="Grants">{data.activeGrantCount} non-cancelled · {data.grantCount - data.activeGrantCount} cancelled</Field>
          {funding && <>
            <Field label="Next release date">{date(funding.nextUnlockTime, 'No future release scheduled')}</Field>
            <Field label={funding.nextUnlockTime > 0n ? 'Next-period funding' : 'Funding required now'}>
              {tokens(funding.nextPeriodFunding)}
            </Field>
          </>}
          <Field label="Factory balance">{tokens(data.factoryBalance)}</Field>
          {funding && <>
            <Field label="Additional treasury deposit">{tokens(deposit)}</Field>
            <Field label="Remaining lifetime funding">{tokens(funding.remainingFunding)}</Field>
            <Field label="Current funding shortfall">{tokens(funding.unfundedEarned)}</Field>
          </>}
        </dl>
        <p style={{ fontSize: 12, color: 'var(--text-2)', margin: '20px 0 0' }}>
          Covers all grants, regardless of the selected page. Total allocation excludes cancelled
          grants and includes the full allocation of off-boarded grants.
          {funding ? <> Next-period funding covers the next release plus any current shortfall,
            after deducting tokens already held by each grant. Additional treasury deposit also
            deducts the factory balance. Remaining lifetime funding accounts for off-boarding and
            tokens already claimed or held by grants.</> : <> Periodic funding statistics are not available for this legacy factory.</>}
        </p>
        {funding && funding.unfundedEarned > 0n && <p role="status" style={{ color: 'var(--warn)', fontSize: 12 }}>
          The current funding shortfall is already due; it is included in the funding amount above.
        </p>}
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 0 }}>As of {date(data.asOf)} · Use Refresh for an updated snapshot.</p>
      </>}
    </section>
  );
};

const GrantDetails: React.FC<{ grant: LockDetails }> = ({ grant }) => (
  <dl style={{ ...fieldsStyle, marginTop: 24, borderTop: '1px solid var(--border)', paddingTop: 24 }}>
    <Field label="Grant contract">{grant.address}</Field>
    <Field label="Beneficiary">{grant.beneficiary}</Field>
    <Field label="Track">{categoryLabel(grant.category)}</Field>
    <Field label="Start date">{date(grant.startTime)}</Field>
    <Field label="Unlock cliff">{date(grant.unlockCliffTime)}</Field>
    <Field label="Off-board date">{date(grant.offboardTime, 'Not off-boarded')}</Field>
    <Field label="Allocation">{tokens(grant.totalAmount)}</Field>
    <Field label="Vested">{tokens(displayedVestedAmount(grant))}</Field>
    <Field label="Unlocked by schedule">{tokens(grant.unlockScheduleAmount)}</Field>
    <Field label="Claimed">{tokens(grant.unlockedAmount)}</Field>
    <Field label="Lifetime payout ceiling">{tokens(payableCeiling(grant, Date.now()))}</Field>
    <Field label="Earned, unclaimed">{tokens(grant.earnedUnclaimed)}</Field>
    <Field label="Claimable amount">{tokens(grant.availableAmount)}</Field>
    <Field label="Lock balance">{tokens(grant.lockBalance)}</Field>
    <Field label="Unfunded earned">{tokens(grant.unfundedEarned)}</Field>
    <Field label="Excess funding">{tokens(grant.excessFunding)}</Field>
    <Field label="Next unlock">{date(grant.nextUnlockTime, 'Nothing scheduled')}</Field>
    <Field label="Funding needed for next unlock">{tokens(grant.fundingDueNextUnlock)}</Field>
    <Field label="Initialized">{grant.isInitialized ? 'Yes' : 'No'}</Field>
    <Field label="Paused">{grant.isPaused ? 'Yes' : 'No'}</Field>
    <Field label="Cancelled">{grant.cancelled ? 'Yes' : 'No'}</Field>
  </dl>
);

/** Intentionally unlisted: share #/grants or #/grants/lock/:address directly. */
export const GrantsScreen: React.FC = () => {
  const admin = useAdmin({ readOnly: true });
  const { address: selected = null } = useParams();
  const navigate = useNavigate();
  const ready = admin.dataVersion > 0 && !admin.isLoading && !admin.error;
  const grants = useGrantPages({
    addresses: ready ? admin.allLocks : [],
    selectedAddress: selected,
    revision: `${environment.chainId}:${environment.factoryAddress}:${admin.dataVersion}`,
    load: admin.getAllLockDetails,
  });
  const unknownGrant = ready && selected !== null &&
    !admin.allLocks.some((address) => address.toLowerCase() === selected.toLowerCase());

  const copyLink = async () => {
    const url = new URL(window.location.href);
    url.hash = selected ? `/grants/lock/${selected}` : '/grants';
    try {
      await navigator.clipboard.writeText(url.toString());
      toast.success('Read-only link copied');
    } catch {
      toast.error('Could not copy the link. Copy the address from your browser.');
    }
  };
  const changePage = (page: number) => {
    navigate('/grants', { replace: true });
    grants.setPage(page);
  };

  return (
    <div className="sh-main">
      <div className="sh-main-inner" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div>
          <div className="sh-label">Read-only</div>
          <h1 className="sh-h1">Grant configuration</h1>
          <p className="sh-sub" style={{ marginTop: 14 }}>
            View on-chain grants and schedules. No wallet connection or signature is required.
            All dates are shown in UTC.
          </p>
          <div style={{ display: 'flex', gap: 12, marginTop: 20, flexWrap: 'wrap' }}>
            <button type="button" className="sh-btn" onClick={copyLink}>Copy read-only link</button>
            <button type="button" className="sh-btn" disabled={admin.isLoading}
              onClick={() => admin.loadAdminData()}>Refresh</button>
          </div>
        </div>

        {admin.error ? (
          <div role="alert" className="sh-banner sh-banner-danger">
            <div>Unable to read grant configuration. {admin.error}</div>
            <button type="button" className="sh-btn sh-btn-sm" onClick={() => admin.loadAdminData()}>Retry</button>
          </div>
        ) : !ready ? <Spinner label="Reading grant configuration" /> : (
          <>
            <Summary revision={admin.dataVersion} />
            <section className="sh-card" aria-label="Factory configuration">
              <dl style={fieldsStyle}>
                <Field label="Network">{environment.chainName}</Field>
                <Field label="Factory">{environment.factoryAddress}</Field>
                <Field label="Owner">{admin.factoryOwner}</Field>
                <Field label="Pending owner">{admin.pendingOwner ?? 'None'}</Field>
                <Field label="Investor unlock cliff">{date(admin.unlockCliffTimes[Category.INVESTOR])}</Field>
                <Field label="Team unlock cliff">{date(admin.unlockCliffTimes[Category.TEAM])}</Field>
                <Field label="TGE (reference only)">{date(admin.tgeTime)}</Field>
              </dl>
            </section>

            {unknownGrant && <div role="alert" className="sh-banner sh-banner-warn">
              This grant is not registered with this factory. <Link to="/grants">View all grants</Link>
            </div>}

            <section aria-label="Grants">
              <h2 className="sh-h2">All grants ({admin.allLocks.length})</h2>
              {admin.allLocks.length === 0 ? <p className="sh-cause">No grants created yet.</p> : (
                <>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', margin: '20px 0' }}>
                    <label>
                      Grants per page{' '}
                      <select className="sh-select" style={{ width: 'auto', display: 'inline-block' }}
                        aria-label="Grants per page" value={grants.pageSize} onChange={(event) => {
                          navigate('/grants', { replace: true });
                          grants.setPageSize(Number(event.target.value));
                        }}>
                        {[10, 25, 50].map((size) => <option key={size} value={size}>{size}</option>)}
                      </select>
                    </label>
                    <span aria-live="polite">Page {grants.page + 1} of {grants.pageCount}</span>
                    <button type="button" className="sh-btn sh-btn-sm" disabled={grants.page === 0}
                      onClick={() => changePage(grants.page - 1)}>Previous page</button>
                    <button type="button" className="sh-btn sh-btn-sm" disabled={grants.page + 1 >= grants.pageCount}
                      onClick={() => changePage(grants.page + 1)}>Next page</button>
                  </div>
                  {grants.loading && <p role="status">Loading grant details…</p>}
                  {grants.error && <div role="alert" className="sh-banner sh-banner-danger">
                    {grants.error}
                    <button type="button" className="sh-btn sh-btn-sm" onClick={grants.retry}>Retry details</button>
                  </div>}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {grants.pageAddresses.map((address) => {
                      const grant = grants.details[address];
                      const expanded = selected?.toLowerCase() === address.toLowerCase();
                      return (
                        <article key={address} className="sh-card">
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                            <div style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                              <h3 style={{ margin: 0, fontSize: 16 }}>{grant?.name || address}</h3>
                              {grant ? <>
                                <p style={{ fontSize: 12, color: 'var(--text-2)' }}>Beneficiary: <span className="sh-mono">{grant.beneficiary}</span></p>
                                <p style={{ fontSize: 13, color: 'var(--text-2)' }}>{tokens(grant.totalAmount)} · {categoryLabel(grant.category)} track</p>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  {grant.cancelled ? <span className="sh-pill sh-pill-danger">Cancelled</span> : <>
                                    {grant.offboardTime > 0n && <span className="sh-pill sh-pill-warn">Off-boarded</span>}
                                    <span className={`sh-pill ${grant.isPaused ? 'sh-pill-warn' : 'sh-pill-success'}`}>
                                      {grant.isPaused ? 'Paused' : 'Active'}
                                    </span>
                                    {!grant.isInitialized && <span className="sh-pill sh-pill-warn">Not initialized</span>}
                                  </>}
                                </div>
                              </> : <p className="sh-cause">{grants.loading ? 'Loading…' : 'Details unavailable. Retry above.'}</p>}
                            </div>
                            <Link className="sh-btn sh-btn-sm" aria-expanded={expanded}
                              to={expanded ? '/grants' : `/grants/lock/${address}`} replace>
                              {expanded ? 'Hide details' : 'View details'}
                            </Link>
                          </div>
                          {expanded && grant && <GrantDetails grant={grant} />}
                        </article>
                      );
                    })}
                  </div>
                </>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};
